import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { getSubscription } from '../../../../shared/subscriptions/utils';
import { window } from '../../../globals';
import { useRemoveCustomContributorCount } from '../../../hooks/api/organization/useRemoveCustomContributorCount';
import { useUpdateCustomContributorCount } from '../../../hooks/api/organization/useUpdateCustomContributorCount';
import ButtonGroup from '../../generic/Button/ButtonGroup';
import InlineLoaderButton from '../../generic/Button/InlineLoaderButton';
import Card from '../../generic/Card/Card';
import type { Organization, SetState } from '../../../types';

type CustomContribCountCardProps = {
  org: Organization;
  setOrg: SetState<Organization>;
};

const CustomContribCountCard: VoidFunctionComponent<Omit<CustomContribCountCardProps, 'isOrgLoading'>> = ({
  org,
  setOrg,
}) => {
  const [contributorCount, setContributorCount] = useState(org.custom_contributor_count || org.contributor_count || 0);

  const { mutate: updateContributorCount, isPending: isUpdating } = useUpdateCustomContributorCount(() => {
    setOrg({
      ...org,
      custom_billing: true,
    });
  });

  const { mutate: removeContributorCount, isPending: isRemoving } = useRemoveCustomContributorCount(() => {
    window.location.reload();
  });

  useEffect(() => {
    setContributorCount(org.custom_contributor_count || org.contributor_count || 0);
  }, [org]);

  const hasCustomContributors = useMemo(() => {
    return !!org.custom_contributor_count;
  }, [org]);

  const monthlyCost = useMemo(() => {
    const plan = getSubscription({ plan: org.stripe_plan, stripePriceLimit: org.stripePriceLimit });
    return contributorCount * plan.price;
  }, [contributorCount, org]);

  const submitContributorCount = useCallback(() => {
    updateContributorCount({ contributorCount });
  }, [contributorCount, updateContributorCount]);

  const deleteContributorCount = useCallback(() => {
    removeContributorCount();
  }, [removeContributorCount]);

  const isLoading = isUpdating || isRemoving;

  return (
    <div>
      <input
        type="number"
        value={contributorCount}
        onChange={(event) => {
          setContributorCount(Number(event.target.value));
        }}
      />
      <p>They&#39;ll be charged ${monthlyCost}/month</p>
      <ButtonGroup>
        <InlineLoaderButton type="slate" onClick={submitContributorCount} isLoading={isLoading}>
          Submit
        </InlineLoaderButton>
        <InlineLoaderButton
          type="error"
          onClick={deleteContributorCount}
          isDisabled={!hasCustomContributors}
          isLoading={isLoading}
        >
          Remove custom count
        </InlineLoaderButton>
      </ButtonGroup>
    </div>
  );
};

const ContributorCountCard: VoidFunctionComponent<CustomContribCountCardProps> = ({
  org,
  setOrg,
}) => {
  let content: ReactNode;
  if (org.custom_billing) {
    content = <p>This organization has custom billing.</p>;
  } else {
    content = <CustomContribCountCard org={org} setOrg={setOrg} />;
  }

  return (
    <Card isFullWidth padding="medium">
      <Card.Header>Lock contributor count</Card.Header>
      <Card.Content>
        {content}
      </Card.Content>
    </Card>
  );
};

export default ContributorCountCard;
